import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  createAvailability,
  updateAvailability,
  clearState,
} from "../../redux/apis/Availability/doctorAvailabilitySlice";

import { useNavigate } from "react-router-dom";

import {
  getBusinessUserProfileImageUrl,
} from "../utils/buserimageUtils";

// ============================================================
// CONSTANTS
// ============================================================


const MAX_TIME = "22:00";

const SLOT_DURATION = 30;

const DEFAULT_SESSIONS = [
  {
    name: "Morning",
    start: "06:00",
    end: "12:00",
  },
  {
    name: "Afternoon",
    start: "12:00",
    end: "15:00",
  },
  {
    name: "Evening",
    start: "15:00",
    end: "19:00",
  },
  {
    name: "Night",
    start: "19:00",
    end: "22:00",
  },
];





const ROUND_DURATIONS = [
  {
    label: "30 Minutes",
    value: 30,
  },
  {
    label: "1 Hour",
    value: 60,
  },
  {
    label: "2 Hours",
    value: 120,
  },
  {
    label: "3 Hours",
    value: 180,
  },
];

const OPERATION_DURATIONS = [
  {
    label: "30 Minutes",
    value: 30,
  },
  {
    label: "1 Hour",
    value: 60,
  },
  {
    label: "1.5 Hours",
    value: 90,
  },
  {
    label: "2 Hours",
    value: 120,
  },
  {
    label: "3 Hours",
    value: 180,
  },
  {
    label: "4 Hours",
    value: 240,
  },
];

// ============================================================
// DATE UTILITIES
// ============================================================

const getLocalDateString = (
  date = new Date()
) => {
  const y = date.getFullYear();

  const m = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const d = String(
    date.getDate()
  ).padStart(2, "0");

  return `${y}-${m}-${d}`;
};

const getWeekWithDates = () => {
  const result = [];

  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const d = new Date(today);

    d.setDate(
      today.getDate() + i
    );

    result.push({
      day: d.toLocaleDateString(
        "en-US",
        {
          weekday: "long",
        }
      ),

      date: getLocalDateString(d),

      // NEW DAYS ARE WORKING DAYS BY DEFAULT
      isHoliday: false,

      // Create the default patient sessions for the entire week.
      // Doctor can later change any individual day to Day Off.
      sessions: createDefaultSessions(
        getLocalDateString(d),
        [],
        []
      ),

      rounds: [],

      operationSchedules: [],
    });
  }

  return result;
};

// ============================================================
// TIME UTILITIES
// ============================================================

const timeToMinutes = (
  time
) => {
  if (
    !time ||
    !time.includes(":")
  ) {
    return null;
  }

  const [
    hours,
    minutes,
  ] = time
    .split(":")
    .map(Number);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }

  return (
    hours * 60 +
    minutes
  );
};

const minutesToTime = (
  minutes
) => {
  const h = Math.floor(
    minutes / 60
  );

  const m = minutes % 60;

  return `${String(h).padStart(
    2,
    "0"
  )}:${String(m).padStart(
    2,
    "0"
  )}`;
};

const addMinutes = (
  time,
  minutes
) => {
  const value =
    timeToMinutes(time);

  if (value === null) {
    return "";
  }

  return minutesToTime(
    value + Number(minutes)
  );
};

// ============================================================
// FORMAT TIME
// ============================================================

const formatTime = (
  time,
  mode = "24"
) => {
  if (!time) {
    return "--";
  }

  const [
    h,
    m,
  ] = time
    .split(":")
    .map(Number);

  if (
    Number.isNaN(h) ||
    Number.isNaN(m)
  ) {
    return "--";
  }

  if (mode === "24") {
    return `${String(h).padStart(
      2,
      "0"
    )}:${String(m).padStart(
      2,
      "0"
    )}`;
  }

  const suffix =
    h >= 12 ? "PM" : "AM";

  const hour =
    h % 12 || 12;

  return `${String(hour).padStart(
    2,
    "0"
  )}:${String(m).padStart(
    2,
    "0"
  )} ${suffix}`;
};

// ============================================================
// DATE / TIME VALIDATION
// ============================================================

const isPastDate = (
  date
) => {
  return (
    date <
    getLocalDateString()
  );
};

const isToday = (
  date
) => {
  return (
    date ===
    getLocalDateString()
  );
};

const getCurrentMinutes = () => {
  const now = new Date();

  return (
    now.getHours() * 60 +
    now.getMinutes()
  );
};

const isTimePast = (
  date,
  time
) => {
  if (!date || !time) {
    return false;
  }

  if (isPastDate(date)) {
    return true;
  }

  if (!isToday(date)) {
    return false;
  }

  const target =
    timeToMinutes(time);

  if (target === null) {
    return false;
  }

  return (
    target <=
    getCurrentMinutes()
  );
};

const isRangePast = (
  date,
  startTime,
  endTime
) => {
  if (!date) {
    return true;
  }

  if (isPastDate(date)) {
    return true;
  }

  if (!isToday(date)) {
    return false;
  }

  const start =
    timeToMinutes(startTime);

  const end =
    timeToMinutes(endTime);

  if (
    start === null ||
    end === null
  ) {
    return true;
  }

  if (
    end <=
    getCurrentMinutes()
  ) {
    return true;
  }

  if (
    start <=
    getCurrentMinutes()
  ) {
    return true;
  }

  return false;
};

const isValidTimeRange = (
  start,
  end
) => {
  const s =
    timeToMinutes(start);

  const e =
    timeToMinutes(end);

  if (
    s === null ||
    e === null
  ) {
    return false;
  }

  return e > s;
};

// ============================================================
// OVERLAP
// ============================================================

const rangesOverlap = (
  startA,
  endA,
  startB,
  endB
) => {
  const aStart =
    timeToMinutes(startA);

  const aEnd =
    timeToMinutes(endA);

  const bStart =
    timeToMinutes(startB);

  const bEnd =
    timeToMinutes(endB);

  if (
    aStart === null ||
    aEnd === null ||
    bStart === null ||
    bEnd === null
  ) {
    return false;
  }

  return (
    aStart < bEnd &&
    bStart < aEnd
  );
};

// ============================================================
// TIME INPUT
// ============================================================

const TimeInput = ({
  value,
  onChange,
  disabled = false,
  mode = "24",
  className = "",
}) => {
  return (
    <div
      className={`relative ${className}`}
    >
      <input
        type="time"
        value={value || ""}
        disabled={disabled}
        onChange={(e) =>
          onChange(e.target.value)
        }
        className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-12 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100 disabled:text-slate-400"
      />

      <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] font-bold text-slate-400 bg-white px-1">
        {mode === "24"
          ? "24H"
          : "12H"}
      </span>
    </div>
  );
};



// ============================================================
// ERROR POPUP
// ============================================================

const ErrorPopup = ({
  message,
  onClose,
}) => {
  if (!message) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-red-200">
        <div className="bg-red-600 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white text-xl">
            !
          </div>

          <div>
            <h3 className="text-lg font-bold text-white">
              Availability Error
            </h3>

            <p className="text-xs text-red-100">
              Please correct the schedule before continuing.
            </p>
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm leading-6 text-slate-700 whitespace-pre-line">
            {message}
          </p>

          <button
            type="button"
            onClick={onClose}
            className="w-full mt-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// SUCCESS POPUP
// ============================================================

const SuccessPopup = ({
  message,
  onClose,
}) => {
  if (!message) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-green-200">
        <div className="bg-green-600 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white text-xl">
            ✓
          </div>

          <h3 className="text-lg font-bold text-white">
            Success
          </h3>
        </div>

        <div className="p-5">
          <p className="text-sm leading-6 text-slate-700">
            {message}
          </p>

          <button
            type="button"
            onClick={onClose}
            className="w-full mt-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// GET OPERATION SCHEDULES
// ============================================================

const getOperationSchedules = (
  day
) => {
  if (
    Array.isArray(
      day?.operationSchedules
    )
  ) {
    return day.operationSchedules;
  }

  if (
    Array.isArray(
      day?.operations
    )
  ) {
    return day.operations;
  }

  return [];
};

// ============================================================
// BLOCKED RANGES
// ============================================================

const getBlockedRanges = (
  day
) => {
  const ranges = [];

  // ----------------------------------------------------------
  // DOCTOR ROUNDS
  // ----------------------------------------------------------

  (
    day?.rounds || []
  ).forEach((round) => {
    if (
      round?.startTime &&
      round?.endTime
    ) {
      ranges.push({
        type: "round",
        label: "Doctor Round",
        startTime:
          round.startTime,
        endTime:
          round.endTime,
      });
    }
  });

  // ----------------------------------------------------------
  // OPERATIONS
  // ----------------------------------------------------------

  const operations =
    getOperationSchedules(day);

  operations.forEach(
    (operation) => {
      if (
        operation?.startTime &&
        operation?.endTime
      ) {
        ranges.push({
          type: "operation",
          label: "Operation",
          startTime:
            operation.startTime,
          endTime:
            operation.endTime,
        });
      }
    }
  );

  return ranges;
};

// ============================================================
// BLOCKED RANGE VALIDATION
// ============================================================

const validateBlockedRanges = (
  rounds,
  operations,
  timeMode = "24"
) => {
  const all = [];

  (
    rounds || []
  ).forEach((round) => {
    all.push({
      type: "Doctor Round",
      startTime:
        round.startTime,
      endTime:
        round.endTime,
    });
  });

  (
    operations || []
  ).forEach((operation) => {
    all.push({
      type: "Operation",
      startTime:
        operation.startTime,
      endTime:
        operation.endTime,
    });
  });

  for (
    let i = 0;
    i < all.length;
    i++
  ) {
    if (
      !isValidTimeRange(
        all[i].startTime,
        all[i].endTime
      )
    ) {
      return `${all[i].type} has an invalid time range. Start time must be before end time.`;
    }

    for (
      let j = i + 1;
      j < all.length;
      j++
    ) {
      if (
        rangesOverlap(
          all[i].startTime,
          all[i].endTime,
          all[j].startTime,
          all[j].endTime
        )
      ) {
        return `${all[i].type} ${formatTime(
          all[i].startTime,
          timeMode
        )} - ${formatTime(
          all[i].endTime,
          timeMode
        )} overlaps with ${all[j].type} ${formatTime(
          all[j].startTime,
          timeMode
        )} - ${formatTime(
          all[j].endTime,
          timeMode
        )}. Please choose different timings.`;
      }
    }
  }

  return null;
};

// ============================================================
// SLOT GENERATOR
// ============================================================

const generatePatientSlots = (
  start,
  end,
  duration,
  date,
  blockedRanges = []
) => {
  const slots = [];

  const startMinutes =
    timeToMinutes(start);

  const endMinutes =
    timeToMinutes(end);

  if (
    startMinutes === null ||
    endMinutes === null ||
    endMinutes <= startMinutes
  ) {
    return slots;
  }

  for (
    let current = startMinutes;
    current + duration <=
    endMinutes;
    current += duration
  ) {
    const slotStart =
      minutesToTime(current);

    const slotEnd =
      minutesToTime(
        current + duration
      );

    // --------------------------------------------------------
    // NEVER GENERATE PAST SLOT
    // --------------------------------------------------------

    if (
      isTimePast(
        date,
        slotStart
      )
    ) {
      continue;
    }

    // --------------------------------------------------------
    // NEVER GENERATE BLOCKED SLOT
    // --------------------------------------------------------

    const blocked =
      blockedRanges.some(
        (range) =>
          rangesOverlap(
            slotStart,
            slotEnd,
            range.startTime,
            range.endTime
          )
      );

    if (blocked) {
      continue;
    }

    slots.push({
      startTime: slotStart,
      endTime: slotEnd,
      isBooked: false,
    });
  }

  return slots;
};

// ============================================================
// CREATE DEFAULT SESSIONS
// ============================================================

const createDefaultSessions = (
  date,
  rounds = [],
  operationSchedules = []
) => {
  const blockedRanges = [
    ...(rounds || []).map(
      (item) => ({
        startTime:
          item.startTime,
        endTime:
          item.endTime,
      })
    ),

    ...(operationSchedules || []).map(
      (item) => ({
        startTime:
          item.startTime,
        endTime:
          item.endTime,
      })
    ),
  ];

  return DEFAULT_SESSIONS.map(
    (session) => ({
      name: session.name,
      start: session.start,
      end: session.end,

      slots:
        generatePatientSlots(
          session.start,
          session.end,
          SLOT_DURATION,
          date,
          blockedRanges
        ),
    })
  );
};

// ============================================================
// NORMALIZE DAY
// ============================================================

const normalizeDay = (
  baseDay,
  existingDay
) => {
  if (!existingDay) {
    return {
      ...baseDay,

      // If the database has no record for this day,
      // treat it as a WORKING DAY by default.
      isHoliday: false,

      sessions: createDefaultSessions(
        baseDay.date,
        [],
        []
      ),

      rounds: [],
      operationSchedules: [],
    };
  }

  const normalizedOperations =
    Array.isArray(
      existingDay.operationSchedules
    )
      ? existingDay.operationSchedules
      : Array.isArray(
          existingDay.operations
        )
      ? existingDay.operations
      : [];

  return {
    ...baseDay,
    ...existingDay,

    rounds:
      Array.isArray(
        existingDay.rounds
      )
        ? existingDay.rounds
        : [],

    operationSchedules:
      normalizedOperations,

    sessions:
      Array.isArray(
        existingDay.sessions
      )
        ? existingDay.sessions
        : [],
  };
};

// ============================================================
// COMPONENT
// ============================================================

const DoctorAvailability = () => {
  const dispatch =
    useDispatch();
const [roundsEnabled, setRoundsEnabled] = useState(false);
const [operationsEnabled, setOperationsEnabled] = useState(false);
  const navigate =
    useNavigate();

  // ==========================================================
  // REDUX
  // ==========================================================

  const reduxUser =
    useSelector(
      (state) =>
        state.auth.user
    );

  const {
    myAvailability,
    error,
    success,
  } = useSelector(
    (state) =>
      state.doctorAvailability
  );

  // ==========================================================
  // TIME MODE
  // ==========================================================

  const [
    timeMode,
    setTimeMode,
  ] = useState(() => {
    return (
      localStorage.getItem(
        "doctorTimeMode"
      ) || "24"
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "doctorTimeMode",
      timeMode
    );
  }, [timeMode]);

  // ==========================================================
  // STATE
  // ==========================================================

  const [
    weekly,
    setWeekly,
  ] = useState(
    getWeekWithDates()
  );

  const [
    selectedDayIndex,
    setSelectedDayIndex,
  ] = useState(0);

  const [
    popupError,
    setPopupError,
  ] = useState("");

  const [
    popupSuccess,
    setPopupSuccess,
  ] = useState("");

  const [
    roundStart,
    setRoundStart,
  ] = useState("09:00");

  const [
    roundDuration,
    setRoundDuration,
  ] = useState(60);

  const [
    operationStart,
    setOperationStart,
  ] = useState("10:00");

  const [
    operationDuration,
    setOperationDuration,
  ] = useState(60);

  const selectedDay =
    weekly[
      selectedDayIndex
    ];

  // ==========================================================
  // ERROR HELPER
  // ==========================================================

  const showError = (
    text
  ) => {
    setPopupError(text);
  };

  // ==========================================================
  // SUCCESS HELPER
  // ==========================================================

  const showSuccess = (
    text
  ) => {
    setPopupSuccess(text);
  };

  // ==========================================================
  // AUTO REFRESH
  // ==========================================================

  useEffect(() => {
    const interval =
      setInterval(() => {
        setWeekly(
          (prev) =>
            structuredClone(prev)
        );
      }, 60000);

    return () =>
      clearInterval(interval);
  }, []);

  // ==========================================================
  // LOAD EXISTING AVAILABILITY
  // ==========================================================

  useEffect(() => {
    const baseWeek =
      getWeekWithDates();

    if (
      !myAvailability?.weeklyAvailability
    ) {
      setWeekly(baseWeek);
      return;
    }

    const merged =
      baseWeek.map(
        (day) => {
          const found =
            myAvailability.weeklyAvailability.find(
              (item) =>
                item.date ===
                day.date
            );

          return normalizeDay(
            day,
            found
          );
        }
      );

    merged.forEach(
      (day) => {
        if (
          isPastDate(
            day.date
          )
        ) {
          day.sessions = [];
          return;
        }

        day.sessions =
          (
            day.sessions ||
            []
          ).map(
            (session) => ({
              ...session,

              slots: (
                session.slots ||
                []
              ).filter(
                (slot) =>
                  !isTimePast(
                    day.date,
                    slot.startTime
                  ) ||
                  slot.isBooked
              ),
            })
          );
      }
    );

    setWeekly(merged);
  }, [
    myAvailability,
  ]);

  // ==========================================================
  // BLOCKED RANGES
  // ==========================================================

  const blockedRanges =
    useMemo(() => {
      if (!selectedDay) {
        return [];
      }

      return getBlockedRanges(
        selectedDay
      );
    }, [
      selectedDay,
    ]);

  // ==========================================================
  // REGENERATE SLOTS
  // ==========================================================

  const regenerateFutureSlots = (
    currentWeek,
    dayIndex
  ) => {
    const copy =
      structuredClone(
        currentWeek
      );

    const day =
      copy[dayIndex];

    if (!day) {
      return copy;
    }

    if (
      isPastDate(day.date)
    ) {
      day.sessions = [];
      return copy;
    }

    const ranges =
      getBlockedRanges(day);

    day.sessions = (
      day.sessions || []
    ).map((session) => {
      const oldSlots =
        Array.isArray(
          session.slots
        )
          ? session.slots
          : [];

      const bookedSlots =
        oldSlots.filter(
          (slot) =>
            slot.isBooked &&
            !isTimePast(
              day.date,
              slot.startTime
            )
        );

      const generated =
        generatePatientSlots(
          session.start,
          session.end,
          SLOT_DURATION,
          day.date,
          ranges
        );

      const merged = [
        ...generated,
      ];

      bookedSlots.forEach(
        (booked) => {
          const exists =
            merged.some(
              (slot) =>
                slot.startTime ===
                  booked.startTime &&
                slot.endTime ===
                  booked.endTime
            );

          if (!exists) {
            merged.push(booked);
          }
        }
      );

      merged.sort(
        (a, b) =>
          timeToMinutes(
            a.startTime
          ) -
          timeToMinutes(
            b.startTime
          )
      );

      return {
        ...session,
        slots: merged,
      };
    });

    return copy;
  };

  // ==========================================================
  // ADD DOCTOR ROUND
  // ==========================================================

  const addDoctorRound = () => {
    if (!selectedDay) {
      showError(
        "Please select a day first."
      );
      return;
    }

    if (
      isPastDate(
        selectedDay.date
      )
    ) {
      showError(
        `${selectedDay.day} (${selectedDay.date}) is already past.\n\nYou cannot add a doctor round to a past day.`
      );
      return;
    }

    const start =
      roundStart;

    if (!start) {
      showError(
        "Please select a doctor round start time."
      );
      return;
    }

    if (
      isTimePast(
        selectedDay.date,
        start
      )
    ) {
      showError(
        `The selected doctor round start time ${formatTime(
          start,
          timeMode
        )} has already passed.\n\nPlease select a future time.`
      );
      return;
    }

    const endTime =
      addMinutes(
        start,
        roundDuration
      );

    if (!endTime) {
      showError(
        "Unable to calculate doctor round end time."
      );
      return;
    }

    if (
      !isValidTimeRange(
        start,
        endTime
      )
    ) {
      showError(
        "Doctor round has an invalid time range.\n\nStart time must be before end time."
      );
      return;
    }

    if (
      timeToMinutes(
        endTime
      ) >
      timeToMinutes(
        MAX_TIME
      )
    ) {
      showError(
        `Doctor round ends at ${formatTime(
          endTime,
          timeMode
        )}.\n\nDoctor rounds cannot go beyond 10:00 PM.`
      );
      return;
    }

    if (
      isRangePast(
        selectedDay.date,
        start,
        endTime
      )
    ) {
      showError(
        `This doctor round (${formatTime(
          start,
          timeMode
        )} - ${formatTime(
          endTime,
          timeMode
        )}) is in the past.\n\nPlease select a future timing.`
      );
      return;
    }

    const newRound = {
      id: `round-${Date.now()}`,

      startTime: start,

      endTime,

      duration:
        Number(
          roundDuration
        ),
    };

    const overlap =
      validateBlockedRanges(
        [
          ...(selectedDay.rounds ||
            []),
          newRound,
        ],
        getOperationSchedules(
          selectedDay
        ),
        timeMode
      );

    if (overlap) {
      showError(
        `Doctor Round conflict:\n\n${overlap}`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[
      selectedDayIndex
    ].rounds = [
      ...(
        copy[
          selectedDayIndex
        ].rounds || []
      ),
      newRound,
    ];

    const updated =
      regenerateFutureSlots(
        copy,
        selectedDayIndex
      );

    setWeekly(updated);
  };

  // ==========================================================
  // ADD OPERATION
  // ==========================================================

  const addOperation = () => {
    if (!selectedDay) {
      showError(
        "Please select a day first."
      );
      return;
    }

    if (
      isPastDate(
        selectedDay.date
      )
    ) {
      showError(
        `${selectedDay.day} (${selectedDay.date}) is already past.\n\nYou cannot add an operation to a past day.`
      );
      return;
    }

    const start =
      operationStart;

    if (!start) {
      showError(
        "Please select an operation start time."
      );
      return;
    }

    if (
      isTimePast(
        selectedDay.date,
        start
      )
    ) {
      showError(
        `The selected operation start time ${formatTime(
          start,
          timeMode
        )} has already passed.\n\nPlease select a future operation time.`
      );
      return;
    }

    const endTime =
      addMinutes(
        start,
        operationDuration
      );

    if (!endTime) {
      showError(
        "Unable to calculate operation end time."
      );
      return;
    }

    if (
      !isValidTimeRange(
        start,
        endTime
      )
    ) {
      showError(
        "Operation has an invalid time range.\n\nStart time must be before end time."
      );
      return;
    }

    if (
      timeToMinutes(
        endTime
      ) >
      timeToMinutes(
        MAX_TIME
      )
    ) {
      showError(
        `Operation ends at ${formatTime(
          endTime,
          timeMode
        )}.\n\nOperations cannot go beyond 10:00 PM.`
      );
      return;
    }

    if (
      isRangePast(
        selectedDay.date,
        start,
        endTime
      )
    ) {
      showError(
        `This operation (${formatTime(
          start,
          timeMode
        )} - ${formatTime(
          endTime,
          timeMode
        )}) is in the past.\n\nPlease select a future timing.`
      );
      return;
    }

    const newOperation = {
      id: `operation-${Date.now()}`,

      title: "Operation",

      startTime: start,

      endTime,

      duration:
        Number(
          operationDuration
        ),

      enabled: true,
    };

    const existingOperations =
      getOperationSchedules(
        selectedDay
      );

    const overlap =
      validateBlockedRanges(
        selectedDay.rounds ||
          [],
        [
          ...existingOperations,
          newOperation,
        ],
        timeMode
      );

    if (overlap) {
      showError(
        `Operation conflict:\n\n${overlap}`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[
      selectedDayIndex
    ] = {
      ...copy[
        selectedDayIndex
      ],

      operationSchedules: [
        ...(
          copy[
            selectedDayIndex
          ]
            .operationSchedules ||
          []
        ),
        newOperation,
      ],
    };

    delete copy[
      selectedDayIndex
    ].operations;

    const updated =
      regenerateFutureSlots(
        copy,
        selectedDayIndex
      );

    setWeekly(updated);
  };

  // ==========================================================
  // REMOVE ROUND
  // ==========================================================

  const removeRound = (
    roundIndex
  ) => {
    if (!selectedDay) {
      return;
    }

    if (
      isPastDate(
        selectedDay.date
      )
    ) {
      showError(
        "Past dates cannot be modified."
      );
      return;
    }

    const round =
      selectedDay.rounds?.[
        roundIndex
      ];

    if (!round) {
      return;
    }

    if (
      isRangePast(
        selectedDay.date,
        round.startTime,
        round.endTime
      )
    ) {
      showError(
        `Past doctor round (${formatTime(
          round.startTime,
          timeMode
        )} - ${formatTime(
          round.endTime,
          timeMode
        )}) cannot be removed.`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[
      selectedDayIndex
    ].rounds.splice(
      roundIndex,
      1
    );

    const updated =
      regenerateFutureSlots(
        copy,
        selectedDayIndex
      );

    setWeekly(updated);
  };

  // ==========================================================
  // REMOVE OPERATION
  // ==========================================================

  const removeOperation = (
    operationIndex
  ) => {
    if (!selectedDay) {
      return;
    }

    if (
      isPastDate(
        selectedDay.date
      )
    ) {
      showError(
        "Past dates cannot be modified."
      );
      return;
    }

    const operations =
      getOperationSchedules(
        selectedDay
      );

    const operation =
      operations[
        operationIndex
      ];

    if (!operation) {
      return;
    }

    if (
      isRangePast(
        selectedDay.date,
        operation.startTime,
        operation.endTime
      )
    ) {
      showError(
        `Past operation (${formatTime(
          operation.startTime,
          timeMode
        )} - ${formatTime(
          operation.endTime,
          timeMode
        )}) cannot be removed.`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[
      selectedDayIndex
    ].operationSchedules =
      operations.filter(
        (_, index) =>
          index !== operationIndex
      );

    delete copy[
      selectedDayIndex
    ].operations;

    const updated =
      regenerateFutureSlots(
        copy,
        selectedDayIndex
      );

    setWeekly(updated);
  };

  // ==========================================================
  // MAKE AVAILABLE
  // ==========================================================

  const makeDayAvailable = (
    index
  ) => {
    const day =
      weekly[index];

    if (!day) {
      return;
    }

    if (
      isPastDate(day.date)
    ) {
      showError(
        `${day.day} (${day.date}) is already past.\n\nPast days cannot be made available.`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[index].isHoliday =
      false;

    const rounds =
      copy[index].rounds ||
      [];

    const operationSchedules =
      getOperationSchedules(
        copy[index]
      );

    copy[index]
      .operationSchedules =
      operationSchedules;

    delete copy[index]
      .operations;

    // --------------------------------------------------------
    // CREATE DEFAULT SESSIONS
    // --------------------------------------------------------

    copy[index].sessions =
      copy[index].sessions?.length
        ? copy[index].sessions
        : createDefaultSessions(
            day.date,
            rounds,
            operationSchedules
          );

    // --------------------------------------------------------
    // REMOVE PAST SLOTS
    // --------------------------------------------------------

    copy[index].sessions =
      copy[index].sessions.map(
        (session) => ({
          ...session,

          slots: (
            session.slots || []
          ).filter(
            (slot) =>
              !isTimePast(
                day.date,
                slot.startTime
              )
          ),
        })
      );

    const updated =
      regenerateFutureSlots(
        copy,
        index
      );

    setWeekly(updated);
  };

  // ==========================================================
  // HOLIDAY
  // ==========================================================

  const makeHoliday = (
    index
  ) => {
    const day =
      weekly[index];

    if (!day) {
      return;
    }

    if (
      isPastDate(day.date)
    ) {
      showError(
        `${day.day} (${day.date}) is already past.\n\nPast days cannot be changed.`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    // IMPORTANT:
    // Holiday must be TRUE.
    copy[index].isHoliday =
      true;

    copy[index].sessions =
      [];

    copy[index].rounds =
      [];

    copy[index]
      .operationSchedules =
      [];

    delete copy[index]
      .operations;

    setWeekly(copy);
  };

  // ==========================================================
  // COPY DAY TO WEEK
  // ==========================================================

  const copyDayToWeek = (
    sourceIndex
  ) => {
    const source =
      weekly[sourceIndex];

    if (!source) {
      showError(
        "Please select a source day first."
      );
      return;
    }

    if (
      isPastDate(source.date)
    ) {
      showError(
        `${source.day} (${source.date}) is a past day.\n\nA past day cannot be copied to future days.`
      );
      return;
    }

    if (source.isHoliday) {
      showError(
        `${source.day} (${source.date}) is marked as Day Off.\n\nA Day Off has no availability schedule to copy. Please select a Working Day.`
      );
      return;
    }

    const sourceHasSlots =
      (
        source.sessions || []
      ).some(
        (session) =>
          Array.isArray(
            session.slots
          ) &&
          session.slots.length >
            0
      );

    if (!sourceHasSlots) {
      showError(
        `${source.day} (${source.date}) has no patient slots.\n\nPlease select a Working Day and configure patient slots before copying.`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    const sourceOperations =
      getOperationSchedules(
        source
      );

    copy.forEach(
      (targetDay, index) => {
        if (
          index ===
          sourceIndex
        ) {
          return;
        }

        if (
          isPastDate(
            targetDay.date
          )
        ) {
          return;
        }

        if (
          targetDay.isHoliday
        ) {
          return;
        }

        const copiedRounds =
          structuredClone(
            source.rounds || []
          );

        const copiedOperations =
          structuredClone(
            sourceOperations
          );

        const copiedSessions =
          createDefaultSessions(
            targetDay.date,
            copiedRounds,
            copiedOperations
          );

        const safeSessions =
          copiedSessions.map(
            (session) => ({
              ...session,

              slots: (
                session.slots ||
                []
              ).filter(
                (slot) =>
                  !isTimePast(
                    targetDay.date,
                    slot.startTime
                  )
              ),
            })
          );

        targetDay.isHoliday =
          false;

        targetDay.rounds =
          copiedRounds;

        targetDay
          .operationSchedules =
          copiedOperations;

        targetDay.sessions =
          safeSessions;

        delete targetDay
          .operations;
      }
    );

    setWeekly(copy);

    showSuccess(
      `The schedule from ${source.day} has been copied to future working days. Day Off days and past days were left unchanged.`
    );
  };

  // ==========================================================
  // REMOVE PATIENT SLOT
  // ==========================================================

  const removePatientSlot = (
    sessionIndex,
    slotIndex
  ) => {
    if (!selectedDay) {
      return;
    }

    const slot =
      selectedDay.sessions?.[
        sessionIndex
      ]?.slots?.[
        slotIndex
      ];

    if (!slot) {
      return;
    }

    if (
      isTimePast(
        selectedDay.date,
        slot.startTime
      )
    ) {
      showError(
        `Patient slot ${formatTime(
          slot.startTime,
          timeMode
        )} - ${formatTime(
          slot.endTime,
          timeMode
        )} has already passed.\n\nPast patient slots cannot be changed.`
      );
      return;
    }

    if (slot.isBooked) {
      showError(
        "This patient slot is already booked and cannot be removed."
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[
      selectedDayIndex
    ].sessions[
      sessionIndex
    ].slots.splice(
      slotIndex,
      1
    );

    setWeekly(copy);
  };

  // ==========================================================
  // UPDATE SESSION TIME
  // ==========================================================

  const updateSessionTime = (
    sessionIndex,
    field,
    value
  ) => {
    if (!selectedDay) {
      return;
    }

    if (
      isPastDate(
        selectedDay.date
      )
    ) {
      showError(
        "Past dates cannot be modified."
      );
      return;
    }

    const session =
      selectedDay.sessions?.[
        sessionIndex
      ];

    if (!session) {
      return;
    }

    const newStart =
      field === "start"
        ? value
        : session.start;

    const newEnd =
      field === "end"
        ? value
        : session.end;

    if (!newStart) {
      showError(
        "Session start time is required."
      );
      return;
    }

    if (!newEnd) {
      showError(
        "Session end time is required."
      );
      return;
    }

    if (
      !isValidTimeRange(
        newStart,
        newEnd
      )
    ) {
      showError(
        `Invalid ${session.name} session timing.\n\nStart: ${formatTime(
          newStart,
          timeMode
        )}\nEnd: ${formatTime(
          newEnd,
          timeMode
        )}\n\nStart time must be before end time.`
      );
      return;
    }

    if (
      timeToMinutes(
        newEnd
      ) >
      timeToMinutes(
        MAX_TIME
      )
    ) {
      showError(
        `${session.name} session cannot go beyond 10:00 PM.`
      );
      return;
    }

    if (
      isToday(
        selectedDay.date
      ) &&
      timeToMinutes(
        newStart
      ) <=
        getCurrentMinutes()
    ) {
      showError(
        `${session.name} session starts at ${formatTime(
          newStart,
          timeMode
        )}, which is already in the past.\n\nPlease select a future session time.`
      );
      return;
    }

    if (
      isToday(
        selectedDay.date
      ) &&
      timeToMinutes(
        newEnd
      ) <=
        getCurrentMinutes()
    ) {
      showError(
        `${session.name} session (${formatTime(
          newStart,
          timeMode
        )} - ${formatTime(
          newEnd,
          timeMode
        )}) is already in the past.\n\nPlease select a future session time.`
      );
      return;
    }

    const ranges =
      getBlockedRanges(
        selectedDay
      );

    const overlappingRange =
      ranges.find(
        (range) =>
          rangesOverlap(
            newStart,
            newEnd,
            range.startTime,
            range.endTime
          )
      );

    if (overlappingRange) {
      showError(
        `${session.name} patient session overlaps with ${overlappingRange.label}.\n\nPatient Session: ${formatTime(
          newStart,
          timeMode
        )} - ${formatTime(
          newEnd,
          timeMode
        )}\n${overlappingRange.label}: ${formatTime(
          overlappingRange.startTime,
          timeMode
        )} - ${formatTime(
          overlappingRange.endTime,
          timeMode
        )}`
      );
      return;
    }

    const copy =
      structuredClone(
        weekly
      );

    copy[
      selectedDayIndex
    ].sessions[
      sessionIndex
    ][field] = value;

    const updated =
      regenerateFutureSlots(
        copy,
        selectedDayIndex
      );

    setWeekly(updated);
  };

  // ==========================================================
  // VALIDATE WEEK BEFORE SAVE
  // ==========================================================
const validateWeekBeforeSave = () => {
  // ============================================================
  // AT LEAST ONE FUTURE AVAILABLE DAY
  // ============================================================

  const availableDays = weekly.filter(
    (day) =>
      !isPastDate(day.date) &&
      day.isHoliday !== true
  );

  if (availableDays.length === 0) {
    return (
      "No available day found.\n\n" +
      "Please select at least one future day and click 'Working Day' before saving."
    );
  }

  // ============================================================
  // VALIDATE EVERY DAY
  // ============================================================

  for (const day of weekly) {
    // ----------------------------------------------------------
    // PAST DAYS
    // ----------------------------------------------------------

    if (isPastDate(day.date)) {
      continue;
    }

    // ----------------------------------------------------------
    // HOLIDAY
    // ----------------------------------------------------------

    if (day.isHoliday === true) {
      continue;
    }

    // ==========================================================
    // OPERATIONS
    // ==========================================================

    const operations = getOperationSchedules(day);

    // ==========================================================
    // ROUNDS
    // ==========================================================

    const rounds = Array.isArray(day.rounds)
      ? day.rounds
      : [];

    // ==========================================================
    // ROUND + OPERATION = BLOCKED TIME
    //
    // IMPORTANT:
    //
    // Round ↔ Operation overlap is ALLOWED.
    //
    // Both have priority over patient slots.
    // ==========================================================

    const blockedRanges = [
      ...rounds.map((round) => ({
        startTime: round.startTime,
        endTime: round.endTime,
        label: "Doctor Round",
        priority: 1,
      })),

      ...operations.map((operation) => ({
        startTime: operation.startTime,
        endTime: operation.endTime,
        label: "Operation",
        priority: 2,
      })),
    ];

    // ==========================================================
    // ROUND PAST CHECK
    // ==========================================================

    for (const round of rounds) {
      if (
        !round?.startTime ||
        !round?.endTime
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          "Doctor Round has an invalid time range."
        );
      }

      if (
        !isValidTimeRange(
          round.startTime,
          round.endTime
        )
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          `Doctor Round ${formatTime(
            round.startTime,
            timeMode
          )} - ${formatTime(
            round.endTime,
            timeMode
          )} has an invalid time range.`
        );
      }

      if (
        isRangePast(
          day.date,
          round.startTime,
          round.endTime
        )
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          `Doctor Round ${formatTime(
            round.startTime,
            timeMode
          )} - ${formatTime(
            round.endTime,
            timeMode
          )} is already in the past.\n\n` +
          "Remove it or change it to a future time."
        );
      }
    }

    // ==========================================================
    // OPERATION VALIDATION
    // ==========================================================

    for (const operation of operations) {
      if (
        !operation?.startTime ||
        !operation?.endTime
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          "Operation has an invalid time range."
        );
      }

      if (
        !isValidTimeRange(
          operation.startTime,
          operation.endTime
        )
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          `Operation ${formatTime(
            operation.startTime,
            timeMode
          )} - ${formatTime(
            operation.endTime,
            timeMode
          )} has an invalid time range.`
        );
      }

      if (
        isRangePast(
          day.date,
          operation.startTime,
          operation.endTime
        )
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          `Operation ${formatTime(
            operation.startTime,
            timeMode
          )} - ${formatTime(
            operation.endTime,
            timeMode
          )} is already in the past.\n\n` +
          "Remove it or change it to a future time."
        );
      }
    }

    // ==========================================================
    // PATIENT SESSIONS
    // ==========================================================

    if (!day.sessions?.length) {
      return (
        `${day.day} (${day.date}) is marked Working Day, ` +
        "but no patient session exists.\n\n" +
        "Please configure patient availability or mark this day as Day Off."
      );
    }

    // ==========================================================
    // PATIENT SESSIONS ARE ALLOWED TO COVER ROUNDS/OPERATIONS
    //
    // Example:
    //
    // Session:
    // 06:00 - 12:00
    //
    // Round:
    // 09:00 - 10:00
    //
    // THIS IS VALID.
    //
    // The 09:00 - 10:00 patient slots are simply blocked.
    // ==========================================================

    for (const session of day.sessions || []) {
      if (
        !isValidTimeRange(
          session.start,
          session.end
        )
      ) {
        return (
          `${day.day} (${day.date})\n\n` +
          `${session.name} session has an invalid time range.`
        );
      }

      // --------------------------------------------------------
      // DO NOT reject session because of Round/Operation.
      //
      // IMPORTANT:
      // There is intentionally NO:
      //
      // ranges.find(...)
      //
      // here.
      //
      // Session can overlap blocked periods.
      // Individual patient slots will be blocked below.
      // --------------------------------------------------------

      for (const slot of session.slots || []) {
        // ------------------------------------------------------
        // NORMAL PAST UNBOOKED SLOT
        // ------------------------------------------------------

        if (
          !slot.isBooked &&
          isTimePast(
            day.date,
            slot.startTime
          )
        ) {
          continue;
        }

        // ------------------------------------------------------
        // ROUND / OPERATION OVERRIDE
        //
        // Patient slot is lower priority.
        //
        // We DO NOT return an error here.
        //
        // The slot should be filtered/disabled before save.
        // ------------------------------------------------------

        const blocked = blockedRanges.find(
          (range) =>
            rangesOverlap(
              slot.startTime,
              slot.endTime,
              range.startTime,
              range.endTime
            )
        );

        if (blocked) {
          // ----------------------------------------------------
          // IMPORTANT:
          //
          // Do not return validation error.
          //
          // Round/Operation wins.
          // ----------------------------------------------------

          continue;
        }
      }
    }

    // ==========================================================
    // FUTURE PATIENT SLOT CHECK
    //
    // Only count slots that are:
    //
    // 1. future
    // 2. OR already booked
    // 3. AND NOT blocked by Round/Operation
    //
    // ==========================================================

    const visibleSlots = (
      day.sessions || []
    ).flatMap((session) =>
      (session.slots || []).filter((slot) => {
        // ------------------------------------------------------
        // Past unbooked slot
        // ------------------------------------------------------

        if (
          !slot.isBooked &&
          isTimePast(
            day.date,
            slot.startTime
          )
        ) {
          return false;
        }

        // ------------------------------------------------------
        // Round / Operation override
        // ------------------------------------------------------

        const blocked = blockedRanges.some(
          (range) =>
            rangesOverlap(
              slot.startTime,
              slot.endTime,
              range.startTime,
              range.endTime
            )
        );

        if (blocked) {
          return false;
        }

        return true;
      })
    );

    // ----------------------------------------------------------
    // IMPORTANT:
    //
    // A day can contain a full patient session but all its slots
    // may be blocked by rounds/operations.
    //
    // That means there is no patient availability left.
    // ----------------------------------------------------------

    if (visibleSlots.length === 0) {
      return (
        `${day.day} (${day.date}) is marked Working Day, ` +
        "but there are no future patient slots available.\n\n" +
        "All patient slots may be blocked by Doctor Rounds or Operations. " +
        "Please configure at least one future patient slot outside those times, " +
        "or mark the day as Day Off."
      );
    }
  }

  // ============================================================
  // EVERYTHING VALID
  // ============================================================

  return null;
};

  // ==========================================================
  // SAVE
  // ==========================================================

  const handleSave =
    async () => {
      if (!reduxUser?._id) {
        showError(
          "Doctor information was not found.\n\nPlease login again and try saving the availability."
        );
        return;
      }

      const validationError =
        validateWeekBeforeSave();

      if (validationError) {
        showError(
          validationError
        );
        return;
      }

      // --------------------------------------------------------
      // CLEAN PAYLOAD
      // --------------------------------------------------------

      const payloadWeekly =
        weekly.map(
          (day) => {
            // --------------------------------------------------
            // PAST DAYS
            // --------------------------------------------------

            if (
              isPastDate(
                day.date
              )
            ) {
              return structuredClone(
                day
              );
            }

            // --------------------------------------------------
            // HOLIDAY
            // --------------------------------------------------

            if (day.isHoliday) {
              return {
                date: day.date,

                day: day.day,

                isHoliday: true,

                rounds: [],

                operationSchedules:
                  [],

                sessions: [],
              };
            }

            // --------------------------------------------------
            // AVAILABLE DAY
            // --------------------------------------------------

            const operations =
              getOperationSchedules(
                day
              );

            return {
              date: day.date,

              day: day.day,

              isHoliday: false,

              rounds:
                day.rounds || [],

              operationSchedules:
                operations,

              sessions:
                (
                  day.sessions ||
                  []
                ).map(
                  (session) => ({
                    ...session,

                    slots:
                      (
                        session.slots ||
                        []
                      ).filter(
                        (slot) =>
                          slot.isBooked ||
                          !isTimePast(
                            day.date,
                            slot.startTime
                          )
                      ),
                  })
                ),
            };
          }
        );

      const payload = {
        doctorId:
          reduxUser._id,

        weeklyAvailability:
          payloadWeekly,
      };

      try {
        const res =
          myAvailability?._id
            ? await dispatch(
                updateAvailability(
                  payload
                )
              )
            : await dispatch(
                createAvailability(
                  payload
                )
              );

        if (
          res?.meta
            ?.requestStatus ===
          "fulfilled"
        ) {
          dispatch(
            clearState()
          );

          showSuccess(
            "Doctor availability has been saved successfully."
          );

          setTimeout(() => {
            navigate(
              "/dashboard/business-user"
            );
          }, 1000);
        } else {
          showError(
            res?.payload ||
              error ||
              "Unable to save doctor availability. Please try again."
          );
        }
      } catch (err) {
        showError(
          err?.message ||
            "Unexpected error while saving doctor availability."
        );
      }
    };

  // ==========================================================
  // SLOT COLOR
  // ==========================================================

  const getSlotColor = (
    slot,
    date
  ) => {
    if (slot.isBooked) {
      return "bg-red-500 text-white";
    }

    if (
      isTimePast(
        date,
        slot.startTime
      )
    ) {
      return "hidden";
    }

    return "bg-sky-500 text-white";
  };

  // ==========================================================
  // USER CHECK
  // ==========================================================

  if (!reduxUser) {
    return (
      <div className="p-6 text-red-600 font-bold">
        User not found
      </div>
    );
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div
      className="min-h-screen p-4 md:p-6"
      style={{
        background:
          "linear-gradient(145deg, rgb(15,31,51) 0%, rgb(26,58,92) 50%, rgb(30,73,118) 100%)",
      }}
    >
      {/* ======================================================
          POPUPS
      ====================================================== */}

      <ErrorPopup
        message={popupError}
        onClose={() =>
          setPopupError("")
        }
      />

      <SuccessPopup
        message={popupSuccess}
        onClose={() =>
          setPopupSuccess("")
        }
      />

      <div className="max-w-[1600px] mx-auto">

        {/* ====================================================
            HEADER
        ==================================================== */}

        <div className="mb-6 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Doctor Availability
            </h1>

            <p className="text-slate-300 mt-1">
              Working Day → Doctor Rounds → Operations → Patient Slots
            </p>
          </div>

          <div className="bg-white/10 border border-white/20 rounded-xl p-2 flex items-center gap-2">
            <span className="text-white text-sm font-semibold px-2">
              Time Format
            </span>

            <button
              type="button"
              onClick={() =>
                setTimeMode("24")
              }
              className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
                timeMode === "24"
                  ? "bg-sky-500 text-white shadow"
                  : "text-slate-300 hover:bg-white/10"
              }`}
            >
              24 Hour
            </button>

            <button
              type="button"
              onClick={() =>
                setTimeMode("12")
              }
              className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
                timeMode === "12"
                  ? "bg-sky-500 text-white shadow"
                  : "text-slate-300 hover:bg-white/10"
              }`}
            >
              12 Hour
            </button>
          </div>
        </div>

        {/* ====================================================
            SCHEDULE HINT
        ==================================================== */}
        <div className="mb-6 rounded-2xl border border-sky-200 bg-sky-50 shadow-lg overflow-hidden">
          <div className="p-4 md:p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center text-lg">
                💡
              </div>
              <div className="min-w-0">
                <h2 className="text-base md:text-lg font-bold text-slate-800">
                  Schedule Hint
                </h2>
                <p className="text-sm text-slate-600 mt-1 leading-6">
                  Days are <span className="font-bold text-red-600">Day Off</span> by default. Select <span className="font-bold text-green-600">Working Day</span> to enable patient appointments and configure your working hours.
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-xs font-semibold">
                  <span className="text-green-700">🟢 Working Day — Patient bookings enabled</span>
                  <span className="text-red-600">🔴 Day Off — No patient bookings</span>
                </div>
              </div>
            </div>
          </div>
        </div>

                {/* ====================================================
            DOCTOR INFORMATION
        ==================================================== */}

        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 mb-6 shadow-xl">
          <div className="flex flex-col md:flex-row items-center gap-5">
            <img
              src={getBusinessUserProfileImageUrl(
                reduxUser.profilePic
              )}
              alt="profile"
              className="w-20 h-20 rounded-full object-cover border-4 border-sky-400"
            />

            <div className="text-white flex-1">
              <h2 className="text-xl font-bold">
                {reduxUser.fullName}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2 mt-2 text-sm text-slate-300">
                <p>
                  <span className="text-white font-semibold">
                    Username:
                  </span>{" "}
                  {reduxUser.username}
                </p>

                <p>
                  <span className="text-white font-semibold">
                    Phone:
                  </span>{" "}
                  {reduxUser.phone}
                </p>

                <p>
                  <span className="text-white font-semibold">
                    Role:
                  </span>{" "}
                  {reduxUser.role}
                </p>

                <p>
                  <span className="text-white font-semibold">
                    Category:
                  </span>{" "}
                  {reduxUser.categoryPath?.join(
                    " → "
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ====================================================
            MAIN
        ==================================================== */}

        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5">

          {/* ==================================================
              WEEK
          ================================================== */}

          <div className="bg-white rounded-2xl shadow-xl p-4 h-fit lg:sticky lg:top-4">
            <h2 className="text-lg font-bold text-slate-800 mb-4">
              Weekly Schedule
            </h2>

            <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 p-3">
              <p className="text-xs font-semibold text-sky-800">
                💡 Schedule Hint
              </p>
              <p className="text-xs text-sky-700 mt-1 leading-5">
                All days are <span className="font-bold">Working Day</span> by default. Select <span className="font-bold">Day Off</span> for holidays, leave, or any day you do not accept patient appointments.
              </p>
            </div>

            <div className="space-y-2">
              {weekly.map(
                (
                  day,
                  index
                ) => {
                  const selected =
                    selectedDayIndex ===
                    index;

                  const past =
                    isPastDate(
                      day.date
                    );

                  return (
                    <button
                      key={
                        day.date
                      }
                      type="button"
                      onClick={() =>
                        setSelectedDayIndex(
                          index
                        )
                      }
                      className={`w-full text-left p-4 rounded-xl transition border ${
                        selected
                          ? "border-sky-500 bg-sky-50 shadow"
                          : "border-slate-200 hover:bg-slate-50"
                      } ${
                        past
                          ? "opacity-60"
                          : ""
                      }`}
                    >
                      <div className="flex justify-between items-center gap-2">
                        <div>
                          <p className="font-bold text-slate-800">
                            {day.day}
                          </p>

                          <p className="text-xs text-slate-500">
                            {day.date}
                          </p>
                        </div>

                        <div>
                          {past ? (
                            <span className="text-xs text-slate-500 font-semibold">
                              Past
                            </span>
                          ) : day.isHoliday ? (
                            <span className="text-xs text-red-500 font-semibold">
                              Day Off
                            </span>
                          ) : (
                            <span className="text-xs text-green-600 font-semibold">
                              Working Day
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                }
              )}
            </div>

            <button
              type="button"
              onClick={() =>
                copyDayToWeek(
                  selectedDayIndex
                )
              }
              className="w-full mt-4 text-white py-2.5 rounded-xl font-semibold"
              style={{
                background:
                  "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
              }}
            >
              Copy Selected Day to Working Week
            </button>
          </div>

          {/* ==================================================
              RIGHT
          ================================================== */}

          <div className="bg-white rounded-2xl shadow-xl p-5 min-w-0">

            {/* DAY HEADER */}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {selectedDay?.day}
                </h2>

                <p className="text-sm text-slate-500">
                  {selectedDay?.date}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={isPastDate(
                    selectedDay?.date
                  )}
                  onClick={() =>
                    makeDayAvailable(
                      selectedDayIndex
                    )
                  }
                  className="px-4 py-2 rounded-lg text-white disabled:opacity-40"
                  style={{
                    background:
                      "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
                  }}
                >
                  Working Day
                </button>

                <button
                  type="button"
                  disabled={isPastDate(
                    selectedDay?.date
                  )}
                  onClick={() =>
                    makeHoliday(
                      selectedDayIndex
                    )
                  }
                  className="px-4 py-2 rounded-lg bg-red-500 text-white disabled:opacity-40"
                >
                  Day Off
                </button>
              </div>
            </div>

            {/* ==================================================
                PAST DAY
            ================================================== */}

            {isPastDate(
              selectedDay?.date
            ) ? (
              <div className="rounded-xl border border-slate-300 bg-slate-100 p-8 text-center">
                <div className="text-4xl mb-2">
                  🕒
                </div>

                <h3 className="text-xl font-bold text-slate-600">
                  Past Day
                </h3>

                <p className="text-sm text-slate-500 mt-2">
                  Past day timings and patient slots are not displayed.
                </p>
              </div>
            ) : selectedDay?.isHoliday ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
                <div className="text-4xl mb-2">
                  🚫
                </div>

                <h3 className="text-xl font-bold text-red-600">
                  Day Off
                </h3>

                <p className="text-sm text-red-500 mt-1">
                  This day is currently a day off. Patients cannot book appointments on this day.
                </p>

                <p className="text-xs text-red-400 mt-3 font-medium">
                  💡 Hint: Select <span className="font-bold">Working Day</span> above to enable patient appointments and configure your working hours.
                </p>
              </div>
            ) : (
              <>
                {/* ==================================================
                    THREE PRIORITY STRUCTURE
                ================================================== */}

              
<div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-start min-w-0">

  {/* ==================================================
      LEFT — ROUNDS + OPERATIONS
  ================================================== */}

  <div className="space-y-3 min-w-0">

    {/* ==================================================
        DOCTOR ROUNDS
    ================================================== */}

    <div
      className={`border rounded-lg p-3 transition-all ${
        roundsEnabled
          ? "border-sky-200 bg-sky-50"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      {/* HEADER */}
      <div className="flex items-center justify-between gap-2 mb-2">

        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-800">
            Doctor Rounds
          </h3>

          <p className="text-[10px] text-slate-500 mt-0.5">
            Round time blocks patient slots.
          </p>
        </div>

        {/* ON / OFF */}
        <div className="shrink-0 flex items-center bg-white border border-slate-200 rounded-md p-0.5">

          <button
            type="button"
            onClick={() => setRoundsEnabled(true)}
            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
              roundsEnabled
                ? "bg-sky-500 text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            ON
          </button>

          <button
            type="button"
            onClick={() => setRoundsEnabled(false)}
            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
              !roundsEnabled
                ? "bg-slate-500 text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            OFF
          </button>

        </div>
      </div>

      {/* STATUS */}
      <div className="mb-2">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            roundsEnabled
              ? "bg-sky-100 text-sky-700"
              : "bg-slate-200 text-slate-600"
          }`}
        >
          {roundsEnabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {/* CONTENT */}
      {roundsEnabled ? (
        <>
          <div className="grid grid-cols-2 gap-2">

            {/* START */}
            <div>
              <label className="text-[10px] font-semibold text-slate-600">
                Start Time
              </label>

              <TimeInput
                value={roundStart}
                onChange={setRoundStart}
                mode={timeMode}
                className="mt-0.5"
              />

              <p className="text-[9px] text-slate-400 mt-0.5">
                {formatTime(roundStart, timeMode)}
              </p>
            </div>

            {/* DURATION */}
            <div>
              <label className="text-[10px] font-semibold text-slate-600">
                Duration
              </label>

              <select
                value={roundDuration}
                onChange={(e) =>
                  setRoundDuration(Number(e.target.value))
                }
                className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-[7px] text-xs bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-400"
              >
                {ROUND_DURATIONS.map((item) => (
                  <option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* ADD */}
          <button
            type="button"
            onClick={addDoctorRound}
            className="w-full mt-2 py-1.5 rounded-md text-white text-xs font-semibold hover:opacity-95"
            style={{
              background:
                "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
            }}
          >
            + Add Round
          </button>

          {/* ROUND LIST */}
          <div className="mt-2 space-y-1.5">

            {(selectedDay.rounds || []).length === 0 ? (
              <p className="text-[10px] text-slate-400">
                No doctor rounds added.
              </p>
            ) : (
              selectedDay.rounds.map((round, index) => (
                <div
                  key={round.id || round._id || index}
                  className="flex items-center justify-between gap-2 bg-white border border-sky-200 rounded-md px-2.5 py-2"
                >

                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800">
                      Doctor Round
                    </p>

                    <p className="text-[10px] text-sky-600">
                      {formatTime(round.startTime, timeMode)}
                      {" - "}
                      {formatTime(round.endTime, timeMode)}

                      <span className="ml-1 text-slate-400">
                        ({Number(round.duration || 0) / 60}h)
                      </span>
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeRound(index)}
                    className="shrink-0 text-red-500 hover:bg-red-50 px-2 py-1 rounded text-[10px] font-medium"
                  >
                    Remove
                  </button>

                </div>
              ))
            )}

          </div>
        </>
      ) : (
        <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-center">
          <p className="text-[10px] font-medium text-slate-500">
            Doctor rounds are OFF
          </p>

          <p className="text-[9px] text-slate-400 mt-0.5">
            Patient slots will not be blocked by rounds.
          </p>
        </div>
      )}
    </div>


    {/* ==================================================
        OPERATIONS
    ================================================== */}

    <div
      className={`border rounded-lg p-3 transition-all ${
        operationsEnabled
          ? "border-purple-200 bg-purple-50"
          : "border-slate-200 bg-slate-50"
      }`}
    >

      {/* HEADER */}
      <div className="flex items-center justify-between gap-2 mb-2">

        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-800">
            Operation Schedule
          </h3>

          <p className="text-[10px] text-slate-500 mt-0.5">
            Operation time blocks patient slots.
          </p>
        </div>

        {/* ON / OFF */}
        <div className="shrink-0 flex items-center bg-white border border-slate-200 rounded-md p-0.5">

          <button
            type="button"
            onClick={() => setOperationsEnabled(true)}
            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
              operationsEnabled
                ? "bg-purple-600 text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            ON
          </button>

          <button
            type="button"
            onClick={() => setOperationsEnabled(false)}
            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
              !operationsEnabled
                ? "bg-slate-500 text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            OFF
          </button>

        </div>
      </div>

      {/* STATUS */}
      <div className="mb-2">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
            operationsEnabled
              ? "bg-purple-100 text-purple-700"
              : "bg-slate-200 text-slate-600"
          }`}
        >
          {operationsEnabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {/* CONTENT */}
      {operationsEnabled ? (
        <>
          <div className="grid grid-cols-2 gap-2">

            {/* START */}
            <div>
              <label className="text-[10px] font-semibold text-slate-600">
                Start Time
              </label>

              <TimeInput
                value={operationStart}
                onChange={setOperationStart}
                mode={timeMode}
                className="mt-0.5"
              />

              <p className="text-[9px] text-slate-400 mt-0.5">
                {formatTime(operationStart, timeMode)}
              </p>
            </div>

            {/* DURATION */}
            <div>
              <label className="text-[10px] font-semibold text-slate-600">
                Duration
              </label>

              <select
                value={operationDuration}
                onChange={(e) =>
                  setOperationDuration(Number(e.target.value))
                }
                className="w-full mt-0.5 border border-slate-300 rounded-md px-2 py-[7px] text-xs bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-purple-400"
              >
                {OPERATION_DURATIONS.map((item) => (
                  <option
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* ADD */}
          <button
            type="button"
            onClick={addOperation}
            className="w-full mt-2 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold"
          >
            + Add Operation
          </button>

          {/* OPERATION LIST */}
          <div className="mt-2 space-y-1.5">

            {getOperationSchedules(selectedDay).length === 0 ? (
              <p className="text-[10px] text-slate-400">
                No operations scheduled.
              </p>
            ) : (
              getOperationSchedules(selectedDay).map(
                (operation, index) => (
                  <div
                    key={
                      operation.id ||
                      operation._id ||
                      index
                    }
                    className="flex items-center justify-between gap-2 bg-white border border-purple-200 rounded-md px-2.5 py-2"
                  >

                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800">
                        Operation {index + 1}
                      </p>

                      <p className="text-[10px] text-purple-600">
                        {formatTime(
                          operation.startTime,
                          timeMode
                        )}
                        {" - "}
                        {formatTime(
                          operation.endTime,
                          timeMode
                        )}

                        <span className="ml-1 text-slate-400">
                          (
                          {Number(operation.duration || 0) / 60}
                          h)
                        </span>
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        removeOperation(index)
                      }
                      className="shrink-0 text-red-500 hover:bg-red-50 px-2 py-1 rounded text-[10px] font-medium"
                    >
                      Remove
                    </button>

                  </div>
                )
              )
            )}

          </div>
        </>
      ) : (
        <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-center">

          <p className="text-[10px] font-medium text-slate-500">
            Operation schedule is OFF
          </p>

          <p className="text-[9px] text-slate-400 mt-0.5">
            Patient slots will not be blocked by operations.
          </p>

        </div>
      )}

    </div>

  </div>


  {/* ==================================================
      RIGHT — PATIENT SLOTS
  ================================================== */}

  <div className="min-w-0">

    <div className="border border-green-200 rounded-lg bg-white overflow-hidden">

      {/* HEADER */}
      <div className="bg-white border-b border-green-200 px-3 py-2">

        <div className="flex items-center justify-between gap-2">

          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">
              Patient Slot Management
            </h3>

            <p className="text-[10px] text-slate-500 mt-0.5">
              Only future slots are displayed.
            </p>
          </div>

          <span className="shrink-0 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold">
            Priority 3
          </span>

        </div>

      </div>


      {/* SLOT CONTENT */}
      <div className="max-h-[600px] overflow-y-auto p-3">

        <div className="space-y-3">

          {(selectedDay.sessions || []).map(
            (session, sessionIndex) => {

              const visibleSlots =
                (session.slots || []).filter(
                  (slot) =>
                    slot.isBooked ||
                    !isTimePast(
                      selectedDay.date,
                      slot.startTime
                    )
                );

              return (
                <div
                  key={sessionIndex}
                  className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50"
                >

                  {/* SESSION HEADER */}
                  <div className="bg-slate-100 border-b border-slate-200 px-3 py-2">

                    <div className="flex items-center justify-between gap-2">

                      <div className="min-w-0">

                        <h4 className="text-xs font-bold text-slate-800">
                          {session.name}
                        </h4>

                        <p className="text-[9px] text-slate-500 mt-0.5">
                          {formatTime(
                            session.start,
                            timeMode
                          )}
                          {" - "}
                          {formatTime(
                            session.end,
                            timeMode
                          )}
                        </p>

                      </div>


                      {/* SESSION TIMES */}
                      <div className="flex items-center gap-1 shrink-0">

                        <div className="w-24">
                          <TimeInput
                            value={session.start}
                            disabled={isPastDate(
                              selectedDay.date
                            )}
                            onChange={(value) =>
                              updateSessionTime(
                                sessionIndex,
                                "start",
                                value
                              )
                            }
                            mode={timeMode}
                          />
                        </div>

                        <span className="text-[10px] text-slate-400">
                          -
                        </span>

                        <div className="w-24">
                          <TimeInput
                            value={session.end}
                            disabled={isPastDate(
                              selectedDay.date
                            )}
                            onChange={(value) =>
                              updateSessionTime(
                                sessionIndex,
                                "end",
                                value
                              )
                            }
                            mode={timeMode}
                          />
                        </div>

                      </div>

                    </div>

                  </div>


                  {/* SLOTS */}
                  <div className="p-2.5">

                    <div className="flex items-center justify-between mb-2">

                      <span className="text-[9px] font-semibold text-slate-500">
                        30-minute slots
                      </span>

                      <span className="text-[9px] font-semibold text-slate-500">
                        {visibleSlots.length} slots
                      </span>

                    </div>


                    <div className="flex flex-wrap gap-1.5">

                      {visibleSlots.length === 0 ? (
                        <p className="text-[10px] text-slate-400">
                          No future patient slots available.
                        </p>
                      ) : (
                        visibleSlots.map((slot) => {

                          const originalIndex =
                            (session.slots || []).findIndex(
                              (item) => item === slot
                            );

                          const slotColor =
                            getSlotColor(
                              slot,
                              selectedDay.date
                            );

                          if (
                            slotColor === "hidden"
                          ) {
                            return null;
                          }

                          return (
                            <div
                              key={`${sessionIndex}-${originalIndex}-${slot.startTime}`}
                              className={`px-2 py-1 rounded-md text-[10px] font-medium ${slotColor}`}
                            >

                              {formatTime(
                                slot.startTime,
                                timeMode
                              )}

                              {" - "}

                              {formatTime(
                                slot.endTime,
                                timeMode
                              )}

                              {slot.isBooked ? (
                                <span className="ml-1">
                                  Booked
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    removePatientSlot(
                                      sessionIndex,
                                      originalIndex
                                    )
                                  }
                                  className="ml-1 bg-black/20 hover:bg-black/40 rounded px-1"
                                  title="Remove slot"
                                >
                                  ×
                                </button>
                              )}

                            </div>
                          );
                        })
                      )}

                    </div>

                  </div>

                </div>
              );
            }
          )}

        </div>

      </div>

    </div>

  </div>

</div>


                {/* ==================================================
                    BLOCKED TIMES
                ================================================== */}

                <div className="border rounded-xl p-4 mt-5 bg-slate-50">

                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-slate-800">
                      Blocked Times
                    </h3>

                    <span className="text-xs text-slate-500">
                      Rounds + Operations
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">

                    {blockedRanges.length ===
                    0 ? (
                      <span className="text-sm text-slate-400">
                        No blocked time.
                      </span>
                    ) : (
                      blockedRanges.map(
                        (
                          range,
                          index
                        ) => (
                          <span
                            key={
                              index
                            }
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                              range.type ===
                              "round"
                                ? "bg-sky-100 text-sky-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {range.label}

                            {" "}

                            {formatTime(
                              range.startTime,
                              timeMode
                            )}

                            {" - "}

                            {formatTime(
                              range.endTime,
                              timeMode
                            )}
                          </span>
                        )
                      )
                    )}

                  </div>
                </div>
              </>
            )}

            {/* ==================================================
                SAVE
            ================================================== */}

            <button
              type="button"
              onClick={
                handleSave
              }
              className="w-full mt-6 py-3 rounded-xl text-white font-bold text-lg shadow-lg"
              style={{
                background:
                  "linear-gradient(135deg, rgb(26,58,92), rgb(14,165,233))",
              }}
            >
              Save Availability
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorAvailability;